/**
 * The gun block of a `<PLANE>.DAT` aircraft record — 33 files, one per
 * aircraft type (docs/formats/missions-campaign.md § *Aircraft data*).
 *
 * This decoder deliberately reads **only the armament**: the gun-position
 * count at 216, the effectiveness/jam pair at 206/208, and the `16 × n`
 * gun-position blocks that start at 218. The rest of the 234-byte record is
 * flight-model data that lives, transcribed, in
 * `packages/engine/src/flight/aircraft-table.ts` — see that file's header for
 * why the fleet table is a hand-transcription rather than a bake stage today
 * (AD-007e). What is here is the half the engine could *not* transcribe,
 * because it is variable-length.
 *
 * ## Two invariants worth checking at decode time, not asserting in a doc
 *
 * `size === 234 + 16 × (positions − 1)` and `mountSlotHigh === mountClass + 1`
 * both hold on all 33 files and on all 44 gun records. They are validated
 * here rather than trusted, on the paint-table lesson's rule
 * (docs/engineering/lessons.md, 2026-08-19 paint/shading bake lane): a struct
 * read at a hard-coded offset should check two cheap invariants so that
 * "wrong file / wrong offset" fails by name instead of producing plausible
 * numbers.
 *
 * ## The position fields are in `.TBL` model units, not a private scale
 *
 * `+0/+2/+4` are a point in the aircraft's own **model** frame — X span
 * (right positive), Y longitudinal (nose positive), Z up, ≈65 units per metre
 * (docs/formats/models-tbl.md §4.2) — sharing the `.TBL` origin. Established
 * by three independent checks over all 44 records, all in
 * docs/formats/missions-campaign.md § *Gun position block*: every record lies
 * inside its own model's bounding box; every tractor aircraft's fixed forward
 * gun sits 0-31 units *behind* its propeller-disc plane while both pushers
 * (`AIRCO`, `FE2`) sit well ahead of their rear propellers; and every flexible
 * gun lands on the crew bay its model's own pilot/observer sphere nodes mark.
 * The consumer that converts them to metres and to the engine's Y-up body
 * frame is `packages/engine`'s `aircraft-spec.ts`.
 */

const NAME_LENGTH = 20;
/** Base record size, i.e. one gun position. `size === BASE_SIZE + GUN_BLOCK_SIZE × (positions − 1)` on all 33 files. */
export const PLANE_DAT_BASE_SIZE = 234;
export const GUN_BLOCK_SIZE = 16;
export const GUN_BLOCK_OFFSET = 218;
export const GUN_EFFECTIVENESS_OFFSET = 206;
export const GUN_JAM_THRESHOLD_OFFSET = 208;
/**
 * Three `int16` at 210/212/214 — `mg.c`'s *real* jam table (AD-011 §4.1,
 * `gun-mounts-design.md`), read at `mg.c:0x44b` and selected by a per-shot
 * field (`aircraft[+0x6a]`) this port has not traced. `GUN_JAM_THRESHOLD_OFFSET`
 * (208) sits in the same gun-parameter block but its own "read by" column in
 * AD-011's static trace is empty — nothing in `mg.c` was found reading it for
 * jamming, so treating 208 as *the* jam threshold (this decoder's older
 * assumption) is not supported by the disassembly. Decoded here as data
 * regardless: all three values are identical on every one of the 33 files
 * (`packages/formats/test/plane-dat.test.ts`), so there is no per-aircraft
 * fleet table to transcribe — only the selector is missing.
 */
export const GUN_JAM_TABLE_OFFSET = 210;
export const GUN_JAM_TABLE_LENGTH = 3;
export const GUN_POSITION_COUNT_OFFSET = 216;

/**
 * The two screen rectangles at 180-187 and 188-195, which
 * `docs/formats/missions-campaign.md` had listed only as "viewport constants
 * … 160 = screen half-width". They are not half-widths: each is four `int16`
 * in `x0, x1, y0, y1` order, and read that way both are rectangles of the
 * 320x200 frame.
 *
 * - **188-195 is the 3D window**: `x 0..319, y 0..160` on all 33 files.
 * - **180-187 is the part of it that aircraft's cockpit art leaves
 *   unobstructed**: `x 0..319` on all 33, with a per-airframe `y` band —
 *   `0..139` for the Camel's cowl, `16..157` for the Albatros family's
 *   centre-section cut-out, `21..139` for the two SPADs, `5..111` for the
 *   two-seaters and the Dr.I, `0..159` for the D.H.2, the fleet's one pusher,
 *   whose pilot sits ahead of everything.
 *
 * The second is what identifies the first: a pair that shares `x 0..319` and
 * differs only in how much of `y 0..160` each aeroplane leaves visible is a
 * world window and the hole its panel leaves in it. The consequence the port
 * cares about is that the window's **centre is row 80, not row 100** — which
 * is where every cockpit paints its gunsight, and therefore where a fixed
 * gun's rounds converge (`apps/web/src/flight/cockpit-projection.ts`).
 */
export const WORLD_WINDOW_OFFSET = 188;
export const VISIBLE_WINDOW_OFFSET = 180;

/**
 * The `+6` byte: which muzzle slot pair the position draws its flashes and
 * tracers from. Only these four values occur, and `+7` is always `+6 plus 1`
 * — a consecutive *pair*, which is what says "index into a slot table" rather
 * than RBHEX4's "number of tracers".
 *
 * The mapping is INFERRED, and its evidence is the cockpit art: across the 19
 * flyable aircraft with a baked cockpit composition, `TWIN_CENTRED` is exactly
 * the set whose `.TTM` paints **two** muzzle flashes and the other three
 * classes are exactly the set that paints **one**, on the side the class
 * names (`SINGLE_LEFT` → all three left of the panel centre, `SINGLE_RIGHT` →
 * all four right of it). 19/19.
 */
export const GUN_MOUNT_CLASSES = {
  TWIN_CENTRED: 0,
  SINGLE_LEFT: 2,
  SINGLE_RIGHT: 4,
  SINGLE_CENTRED: 6,
} as const;

const MOUNT_CLASS_VALUES: readonly number[] = Object.values(GUN_MOUNT_CLASSES);

/** How many barrels a mount class fires from — 2 for the centred-twin class, 1 for the rest. */
export function barrelsForMountClass(mountClass: number): number {
  return mountClass === GUN_MOUNT_CLASSES.TWIN_CENTRED ? 2 : 1;
}

/**
 * Widest total arc, in BAM16, that still counts as a **fixed** gun.
 *
 * The fleet has exactly two fixed-cone widths — 1000 (±500) and 1820 (±910,
 * the Albatros D.Va) — and the narrowest flexible mount is the Gotha's ventral
 * gun at 5500. Nothing lies between 1820 and 5500, so any threshold in that
 * gap classifies all 44 records identically; 2000 is chosen for being a round
 * number just above the widest fixed cone.
 */
export const FIXED_GUN_MAX_ARC_BAM = 2000;

/**
 * True for a gunner's mount rather than a fixed gun. Two shapes qualify: an
 * arc that **wraps** through the rear (`from > to`, e.g. 24000 → −24000, which
 * is 131.8° round to 228.2°), and a symmetric but **wide** arc — the F.E.2b's
 * front-nacelle Lewis at ±22°/±44° and the two bombers' nose guns at ±27°/±22°
 * are Scarff-ring mounts that happen not to wrap.
 *
 * By this test five aircraft — `BE2`, `AVIATIK`, `FE2`, `GOTHA`, `HANDLEY` —
 * have **no fixed forward gun at all**, which is exactly the set whose real
 * armament was the observer's gun alone.
 */
export function isFlexibleMount(gun: PlaneGunBlock): boolean {
  if (gun.horizontalFromBam > gun.horizontalToBam) return true;
  return gun.verticalToBam - gun.verticalFromBam > FIXED_GUN_MAX_ARC_BAM;
}

/** One 16-byte gun position, at `218 + 16 × n`. */
export interface PlaneGunBlock {
  /** `+0` — model-frame X of the tracer origin, right positive. 0 on 39 of the 44 records. */
  readonly rightUnits: number;
  /** `+2` — model-frame Y, nose positive. */
  readonly forwardUnits: number;
  /** `+4` — model-frame Z, up positive. Negative only on the Gotha's and Handley Page's ventral tunnel guns. */
  readonly upUnits: number;
  /** `+6` — one of `GUN_MOUNT_CLASSES`. */
  readonly mountClass: number;
  /** `+7` — always `mountClass + 1`; kept so the pair can be re-checked rather than assumed. */
  readonly mountSlotHigh: number;
  /**
   * `+8`…`+14` — the mount's **traverse arcs**, BAM16 (`65536 = 360°`): `+8/+10` elevation,
   * `+12/+14` azimuth. A fixed gun is the symmetric ±500 band; a gunner's station carries an
   * elevation arc that excludes zero and an azimuth arc that wraps through the rear
   * (`from > to`). See `packages/engine`'s `combat/traverse.ts` — they are not a dispersion cone.
   */
  readonly verticalFromBam: number;
  readonly verticalToBam: number;
  readonly horizontalFromBam: number;
  readonly horizontalToBam: number;
}

/** A screen rectangle of the 320x200 frame, inclusive at both ends — `x0, x1, y0, y1` as stored. */
export interface PlaneScreenRect {
  readonly left: number;
  readonly right: number;
  readonly top: number;
  readonly bottom: number;
}

export interface PlaneDat {
  /** Offsets 0-19, NUL-terminated ASCII. */
  readonly name: string;
  /** Offsets 188-195 — the 3D window, `x 0..319, y 0..160` on all 33 files. See `WORLD_WINDOW_OFFSET`. */
  readonly worldWindow: PlaneScreenRect;
  /** Offsets 180-187 — the part of that window this airframe's cockpit art leaves unobstructed. */
  readonly visibleWindow: PlaneScreenRect;
  /** Offset 206 — gun effectiveness, a damage multiplier: 800 / 900 / 1024 / 1300. */
  readonly gunEffectiveness: number;
  /** Offset 208 — 200 on every one of the 33 files. Not confirmed to be jam-related; see `GUN_JAM_TABLE_OFFSET`. */
  readonly jamThreshold: number;
  /**
   * Offsets 210/212/214 — `mg.c`'s three-entry jam table, `[40, 20, 30]` on
   * every one of the 33 files. See `GUN_JAM_TABLE_OFFSET`'s doc for what is
   * and is not known about how `mg.c` picks among them.
   */
  readonly jamThresholdTable: readonly [number, number, number];
  /** Offset 216 — how many *gun stations* (not barrels) the aircraft has: 1 / 2 / 3. */
  readonly gunPositions: number;
  /** `gunPositions` blocks. On a multi-station aircraft, station 0 is flexible and the last is the fixed forward gun. */
  readonly guns: readonly PlaneGunBlock[];
}

function int16(bytes: Uint8Array, offset: number): number {
  const value = bytes[offset]! | (bytes[offset + 1]! << 8);
  return value >= 0x8000 ? value - 0x10000 : value;
}

function readName(bytes: Uint8Array): string {
  const field = bytes.subarray(0, NAME_LENGTH);
  const end = field.indexOf(0);
  return new TextDecoder().decode(end < 0 ? field : field.subarray(0, end));
}

function readScreenRect(bytes: Uint8Array, at: number): PlaneScreenRect {
  return {
    left: int16(bytes, at + 0),
    right: int16(bytes, at + 2),
    top: int16(bytes, at + 4),
    bottom: int16(bytes, at + 6),
  };
}

function readGunBlock(bytes: Uint8Array, index: number): PlaneGunBlock {
  const at = GUN_BLOCK_OFFSET + GUN_BLOCK_SIZE * index;
  const mountClass = bytes[at + 6]!;
  const mountSlotHigh = bytes[at + 7]!;
  if (!MOUNT_CLASS_VALUES.includes(mountClass) || mountSlotHigh !== mountClass + 1) {
    throw new Error(
      `<PLANE>.DAT: gun ${index} mount pair is (${mountClass}, ${mountSlotHigh}); expected one of ` +
        `${MOUNT_CLASS_VALUES.join("/")} followed by that value plus one`,
    );
  }
  return {
    rightUnits: int16(bytes, at + 0),
    forwardUnits: int16(bytes, at + 2),
    upUnits: int16(bytes, at + 4),
    mountClass,
    mountSlotHigh,
    verticalFromBam: int16(bytes, at + 8),
    verticalToBam: int16(bytes, at + 10),
    horizontalFromBam: int16(bytes, at + 12),
    horizontalToBam: int16(bytes, at + 14),
  };
}

/** Decodes the armament half of one `<PLANE>.DAT`. Throws if the size law or the mount-pair law is broken. */
export function decodePlaneDat(bytes: Uint8Array): PlaneDat {
  if (bytes.length < PLANE_DAT_BASE_SIZE) {
    throw new Error(`<PLANE>.DAT: ${bytes.length} bytes is shorter than the ${PLANE_DAT_BASE_SIZE}-byte base record`);
  }
  const gunPositions = int16(bytes, GUN_POSITION_COUNT_OFFSET);
  const expected = PLANE_DAT_BASE_SIZE + GUN_BLOCK_SIZE * (gunPositions - 1);
  if (gunPositions < 1 || bytes.length !== expected) {
    throw new Error(
      `<PLANE>.DAT: offset ${GUN_POSITION_COUNT_OFFSET} says ${gunPositions} gun position(s), which needs a ` +
        `${expected}-byte record; this file is ${bytes.length} bytes`,
    );
  }
  const jamThresholdTable = Array.from({ length: GUN_JAM_TABLE_LENGTH }, (_, index) =>
    int16(bytes, GUN_JAM_TABLE_OFFSET + index * 2),
  ) as [number, number, number];
  return {
    name: readName(bytes),
    worldWindow: readScreenRect(bytes, WORLD_WINDOW_OFFSET),
    visibleWindow: readScreenRect(bytes, VISIBLE_WINDOW_OFFSET),
    gunEffectiveness: int16(bytes, GUN_EFFECTIVENESS_OFFSET),
    jamThreshold: int16(bytes, GUN_JAM_THRESHOLD_OFFSET),
    jamThresholdTable,
    gunPositions,
    guns: Array.from({ length: gunPositions }, (_, index) => readGunBlock(bytes, index)),
  };
}
